import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { 
  Building2, School, Trees, Plus, Pencil, Trash2, 
  Users, MapPin, Search, Filter, ChevronDown, ChevronRight, BookOpen
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

export default function AdminPartnerSchoolsPage({ 
  embedded = false, 
  defaultTab = 'districts',
  showOnlySchools = false,
  showOnlyLibraries = false,
  showOnlyParks = false
}) {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [districts, setDistricts] = useState([]);
  const [schools, setSchools] = useState([]);
  const [parkDistricts, setParkDistricts] = useState([]);
  const [libraries, setLibraries] = useState([]);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // create or edit
  const [dialogType, setDialogType] = useState('district'); // district, school, park, library
  const [formData, setFormData] = useState({});
  const [expandedDistricts, setExpandedDistricts] = useState({});

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [districtsRes, schoolsRes, parkRes, librariesRes] = await Promise.all([
        api.get('/partner/districts/'),
        api.get('/partner/schools/'),
        api.get('/partner/park-districts/'),
        api.get('/partner/libraries/')
      ]);
      setDistricts(districtsRes.data.results || districtsRes.data);
      setSchools(schoolsRes.data.results || schoolsRes.data);
      setParkDistricts(parkRes.data.results || parkRes.data);
      setLibraries(librariesRes.data.results || librariesRes.data);
    } catch (error) {
      toast.error('Failed to load partner data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenDialog = (type, mode, item = null) => {
    setDialogType(type);
    setDialogMode(mode);
    
    if (mode === 'edit' && item) {
      setFormData({ ...item });
    } else {
      // Default form data for new items
      if (type === 'district') {
        setFormData({ code: '', name: '', contact_name: '', contact_email: '', city: '', state: 'IL', active: true });
      } else if (type === 'school') {
        // If item has a district preset (from Add School button in district), use it
        const presetDistrict = item?.district || null;
        setFormData({ district: presetDistrict, name: '', school_type: 'elementary', grades_served: [], address: '', city: '', state: 'IL', zip_code: '', student_count: 0, active: true });
      } else if (type === 'park') {
        setFormData({ code: '', name: '', contact_name: '', contact_email: '', city: '', state: 'IL', service_area: [], active: true });
      } else if (type === 'library') {
        setFormData({ code: '', name: '', library_system: '', branch_name: '', contact_name: '', contact_email: '', city: '', state: 'IL', service_area: [], active: true });
      }
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const endpoints = {
        district: '/partner/districts/',
        school: '/partner/schools/',
        park: '/partner/park-districts/',
        library: '/partner/libraries/'
      };
      
      const endpoint = endpoints[dialogType];
      const typeLabels = { district: 'District', school: 'School', park: 'Park District', library: 'Library' };
      
      if (dialogMode === 'create') {
        await api.post(endpoint, formData);
        toast.success(`${typeLabels[dialogType]} created successfully`);
      } else {
        await api.put(`${endpoint}${formData.id}/`, formData);
        toast.success(`${typeLabels[dialogType]} updated successfully`);
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (type, id) => {
    if (!confirm('Are you sure you want to delete this item?')) return;
    
    try {
      const endpoints = {
        district: '/partner/districts/',
        school: '/partner/schools/',
        park: '/partner/park-districts/',
        library: '/partner/libraries/'
      };
      
      await api.delete(`${endpoints[type]}${id}/`);
      toast.success('Deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const toggleDistrictExpand = (districtId) => {
    setExpandedDistricts(prev => ({
      ...prev,
      [districtId]: !prev[districtId]
    }));
  };

  // Filter schools based on search and filters
  const filteredSchools = schools.filter(school => {
    const matchesSearch = school.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         school.district_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDistrict = selectedDistrict === 'all' || 
                           (selectedDistrict === 'private' && !school.district) ||
                           school.district === selectedDistrict;
    const matchesType = selectedType === 'all' || school.school_type === selectedType;
    return matchesSearch && matchesDistrict && matchesType;
  });

  const getSchoolTypeLabel = (type) => {
    const labels = {
      'elementary': 'Elementary',
      'middle': 'Middle School',
      'high': 'High School',
      'k8': 'K-8',
      'private': 'Private'
    };
    return labels[type] || type;
  };

  // If showing only specific tabs, filter the view
  const showDistricts = !showOnlyLibraries && !showOnlyParks;
  const showSchools = !showOnlyLibraries && !showOnlyParks;
  const showParks = !showOnlySchools && !showOnlyLibraries;
  const showLibraries = !showOnlySchools && !showOnlyParks;

  return (
    <div className="space-y-6">
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold">Partner Schools & Districts</h1>
            <p className="text-muted-foreground mt-1">
              Manage school districts, partner schools, park districts, and libraries
            </p>
          </div>
        </div>
      )}

      {/* Summary Cards - Only show when not filtered */}
      {!showOnlySchools && !showOnlyLibraries && !showOnlyParks && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-dashboard">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <Building2 className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{districts.length}</p>
                <p className="text-sm text-muted-foreground">School Districts</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-dashboard">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <School className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{schools.length}</p>
                <p className="text-sm text-muted-foreground">Partner Schools</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-dashboard">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 dark:bg-emerald-900/20 rounded-lg">
                <Trees className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{parkDistricts.length}</p>
                <p className="text-sm text-muted-foreground">Park Districts</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-dashboard">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                <BookOpen className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{libraries.length}</p>
                <p className="text-sm text-muted-foreground">Libraries</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {!showOnlySchools && !showOnlyLibraries && !showOnlyParks && (
        <TabsList>
          {showDistricts && (
          <TabsTrigger value="districts" data-testid="tab-districts">
            <Building2 className="h-4 w-4 mr-2" />
            School Districts
          </TabsTrigger>
          )}
          {showSchools && (
          <TabsTrigger value="schools" data-testid="tab-schools">
            <School className="h-4 w-4 mr-2" />
            Partner Schools
          </TabsTrigger>
          )}
          {showParks && (
          <TabsTrigger value="parks" data-testid="tab-parks">
            <Trees className="h-4 w-4 mr-2" />
            Park Districts
          </TabsTrigger>
          )}
          {showLibraries && (
          <TabsTrigger value="libraries" data-testid="tab-libraries">
            <BookOpen className="h-4 w-4 mr-2" />
            Libraries
          </TabsTrigger>
          )}
          )}
        </TabsList>
        )}

        {/* School Districts Tab */}
        {(showDistricts || showOnlySchools) && (
        <TabsContent value="districts">
          <Card className="card-dashboard">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>School Districts</CardTitle>
              <Button onClick={() => handleOpenDialog('district', 'create')} data-testid="add-district-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add District
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">Loading...</div>
              ) : (
                <div className="space-y-2">
                  {districts.map(district => (
                    <div key={district.id} className="border rounded-lg">
                      <div 
                        className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleDistrictExpand(district.id)}
                      >
                        <div className="flex items-center gap-3">
                          {expandedDistricts[district.id] ? 
                            <ChevronDown className="h-4 w-4" /> : 
                            <ChevronRight className="h-4 w-4" />
                          }
                          <div>
                            <p className="font-medium">{district.name}</p>
                            <p className="text-sm text-muted-foreground">{district.code}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <Badge variant="outline">{district.school_count} schools</Badge>
                          <Badge variant={district.active ? "success" : "secondary"}>
                            {district.active ? 'Active' : 'Inactive'}
                          </Badge>
                          <div className="flex gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleOpenDialog('district', 'edit', district); }}
                              data-testid={`edit-district-${district.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleDelete('district', district.id); }}
                              data-testid={`delete-district-${district.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </div>
                      {expandedDistricts[district.id] && (
                        <div className="px-4 pb-4 pt-2 border-t bg-muted/20">
                          <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                            <div>
                              <span className="text-muted-foreground">Contact:</span>
                              <p>{district.contact_name || 'N/A'}</p>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Email:</span>
                              <p>{district.contact_email || 'N/A'}</p>
                            </div>
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-sm">Schools in this District</span>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={(e) => { e.stopPropagation(); handleOpenDialog('school', 'create', { district: district.id }); }}
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                Add School
                              </Button>
                            </div>
                            {schools.filter(s => s.district === district.id).length > 0 ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                {schools.filter(s => s.district === district.id).map(school => (
                                  <div 
                                    key={school.id} 
                                    className="flex items-center justify-between p-3 bg-background rounded-lg border hover:border-primary/50 transition-colors"
                                  >
                                    <div className="flex items-center gap-2">
                                      <School className="h-4 w-4 text-muted-foreground" />
                                      <div>
                                        <p className="font-medium text-sm">{school.name}</p>
                                        <p className="text-xs text-muted-foreground">{getSchoolTypeLabel(school.school_type)}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        className="h-7 w-7 p-0"
                                        onClick={(e) => { e.stopPropagation(); handleOpenDialog('school', 'edit', school); }}
                                      >
                                        <Pencil className="h-3 w-3" />
                                      </Button>
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        className="h-7 w-7 p-0"
                                        onClick={(e) => { e.stopPropagation(); handleDelete('school', school.id); }}
                                      >
                                        <Trash2 className="h-3 w-3 text-destructive" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-4 text-muted-foreground text-sm bg-background rounded-lg border border-dashed">
                                No schools added yet. Click "Add School" to create one.
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {districts.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No school districts found. Add one to get started.
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Partner Schools Tab */}
        {(showSchools || showOnlySchools) && (
        <TabsContent value="schools">
          <Card className="card-dashboard">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Partner Schools</CardTitle>
              <Button onClick={() => handleOpenDialog('school', 'create')} data-testid="add-school-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add School
              </Button>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="flex flex-wrap gap-4 mb-6">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search schools..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="school-search"
                    />
                  </div>
                </div>
                <Select value={selectedDistrict} onValueChange={setSelectedDistrict}>
                  <SelectTrigger className="w-[200px]" data-testid="district-filter">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Filter by District" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Districts</SelectItem>
                    <SelectItem value="private">Private Schools (No District)</SelectItem>
                    {districts.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger className="w-[180px]" data-testid="type-filter">
                    <SelectValue placeholder="School Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="elementary">Elementary</SelectItem>
                    <SelectItem value="middle">Middle School</SelectItem>
                    <SelectItem value="high">High School</SelectItem>
                    <SelectItem value="k8">K-8</SelectItem>
                    <SelectItem value="private">Private School</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Schools Table */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>School Name</TableHead>
                    <TableHead>District</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Grades</TableHead>
                    <TableHead>Students</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSchools.map(school => (
                    <TableRow key={school.id} data-testid={`school-row-${school.id}`}>
                      <TableCell className="font-medium">{school.name}</TableCell>
                      <TableCell>
                        {school.district ? (
                          <Badge variant="outline">{school.district_code}</Badge>
                        ) : (
                          <Badge variant="secondary">Private</Badge>
                        )}
                      </TableCell>
                      <TableCell>{getSchoolTypeLabel(school.school_type)}</TableCell>
                      <TableCell>
                        {Array.isArray(school.grades_served) 
                          ? school.grades_served.join(', ') 
                          : school.grades_served}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          {school.student_count || 0}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={school.active ? "success" : "secondary"}>
                          {school.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleOpenDialog('school', 'edit', school)}
                          data-testid={`edit-school-${school.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDelete('school', school.id)}
                          data-testid={`delete-school-${school.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {filteredSchools.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No schools found matching your filters.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Park Districts Tab */}
        {(showParks || showOnlyParks) && (
        <TabsContent value="parks">
          <Card className="card-dashboard">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Park Districts</CardTitle>
              <Button onClick={() => handleOpenDialog('park', 'create')} data-testid="add-park-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Park District
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Service Area</TableHead>
                    <TableHead>Locations</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parkDistricts.map(park => (
                    <TableRow key={park.id} data-testid={`park-row-${park.id}`}>
                      <TableCell className="font-medium">{park.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{park.code}</Badge>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p>{park.contact_name || 'N/A'}</p>
                          <p className="text-sm text-muted-foreground">{park.contact_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {Array.isArray(park.service_area) && park.service_area.slice(0, 3).map((area, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">{area}</Badge>
                          ))}
                          {Array.isArray(park.service_area) && park.service_area.length > 3 && (
                            <Badge variant="secondary" className="text-xs">+{park.service_area.length - 3}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          {park.location_count || 0}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={park.active ? "success" : "secondary"}>
                          {park.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleOpenDialog('park', 'edit', park)}
                          data-testid={`edit-park-${park.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDelete('park', park.id)}
                          data-testid={`delete-park-${park.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {parkDistricts.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No park districts found. Add one to get started.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Libraries Tab */}
        {(showLibraries || showOnlyLibraries) && (
        <TabsContent value="libraries">
          <Card className="card-dashboard">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Libraries</CardTitle>
              <Button onClick={() => handleOpenDialog('library', 'create')} data-testid="add-library-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Library
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Library System</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Service Area</TableHead>
                    <TableHead>Locations</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {libraries.map(lib => (
                    <TableRow key={lib.id} data-testid={`library-row-${lib.id}`}>
                      <TableCell className="font-medium">{lib.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{lib.code}</Badge>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p>{lib.library_system || 'N/A'}</p>
                          {lib.branch_name && <p className="text-sm text-muted-foreground">{lib.branch_name}</p>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p>{lib.contact_name || 'N/A'}</p>
                          <p className="text-sm text-muted-foreground">{lib.contact_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {Array.isArray(lib.service_area) && lib.service_area.slice(0, 3).map((area, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">{area}</Badge>
                          ))}
                          {Array.isArray(lib.service_area) && lib.service_area.length > 3 && (
                            <Badge variant="secondary" className="text-xs">+{lib.service_area.length - 3}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          {lib.location_count || 0}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={lib.active ? "success" : "secondary"}>
                          {lib.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleOpenDialog('library', 'edit', lib)}
                          data-testid={`edit-library-${lib.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDelete('library', lib.id)}
                          data-testid={`delete-library-${lib.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {libraries.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No libraries found. Add one to get started.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'create' ? 'Add' : 'Edit'}{' '}
              {dialogType === 'district' ? 'School District' : 
               dialogType === 'school' ? 'Partner School' : 
               dialogType === 'library' ? 'Library' : 'Park District'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* District Form */}
            {dialogType === 'district' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>District Code *</Label>
                    <Input
                      value={formData.code || ''}
                      onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                      placeholder="e.g., SD129"
                      data-testid="district-code-input"
                    />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select 
                      value={formData.active ? 'true' : 'false'} 
                      onValueChange={(v) => setFormData({...formData, active: v === 'true'})}
                    >
                      <SelectTrigger data-testid="district-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Active</SelectItem>
                        <SelectItem value="false">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>District Name *</Label>
                  <Input
                    value={formData.name || ''}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., East Aurora School District 131"
                    data-testid="district-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Contact Name</Label>
                    <Input
                      value={formData.contact_name || ''}
                      onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                      data-testid="district-contact-input"
                    />
                  </div>
                  <div>
                    <Label>Contact Email</Label>
                    <Input
                      type="email"
                      value={formData.contact_email || ''}
                      onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                      data-testid="district-email-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>City</Label>
                    <Input
                      value={formData.city || ''}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input
                      value={formData.state || 'IL'}
                      onChange={(e) => setFormData({...formData, state: e.target.value})}
                    />
                  </div>
                </div>
              </>
            )}

            {/* School Form */}
            {dialogType === 'school' && (
              <>
                <div>
                  <Label>School District</Label>
                  <Select 
                    value={formData.district || 'none'} 
                    onValueChange={(v) => setFormData({...formData, district: v === 'none' ? null : v})}
                  >
                    <SelectTrigger data-testid="school-district-select">
                      <SelectValue placeholder="Select a district (optional for private)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No District (Private School)</SelectItem>
                      {districts.map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">Leave empty for private schools not affiliated with a public district</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>School Name *</Label>
                    <Input
                      value={formData.name || ''}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="e.g., Lincoln Elementary"
                      data-testid="school-name-input"
                    />
                  </div>
                  <div>
                    <Label>School Type *</Label>
                    <Select 
                      value={formData.school_type || 'elementary'} 
                      onValueChange={(v) => setFormData({...formData, school_type: v})}
                    >
                      <SelectTrigger data-testid="school-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="elementary">Elementary</SelectItem>
                        <SelectItem value="middle">Middle School</SelectItem>
                        <SelectItem value="high">High School</SelectItem>
                        <SelectItem value="k8">K-8</SelectItem>
                        <SelectItem value="private">Private School</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Address *</Label>
                  <Input
                    value={formData.address || ''}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    data-testid="school-address-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>City *</Label>
                    <Input
                      value={formData.city || ''}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input
                      value={formData.state || 'IL'}
                      onChange={(e) => setFormData({...formData, state: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Zip Code *</Label>
                    <Input
                      value={formData.zip_code || ''}
                      onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Approx. Student Count</Label>
                    <Input
                      type="number"
                      value={formData.student_count || 0}
                      onChange={(e) => setFormData({...formData, student_count: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select 
                      value={formData.active ? 'true' : 'false'} 
                      onValueChange={(v) => setFormData({...formData, active: v === 'true'})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Active</SelectItem>
                        <SelectItem value="false">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Contact Information Section */}
                <div className="border-t pt-4 mt-2">
                  <p className="text-sm font-medium mb-3">Contact Information</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Principal Name</Label>
                      <Input
                        value={formData.principal_name || ''}
                        onChange={(e) => setFormData({...formData, principal_name: e.target.value})}
                        placeholder="e.g., Dr. Jane Smith"
                      />
                    </div>
                    <div>
                      <Label>Program Contact Name</Label>
                      <Input
                        value={formData.contact_name || ''}
                        onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                        placeholder="e.g., John Doe"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <Label>Contact Email</Label>
                      <Input
                        type="email"
                        value={formData.contact_email || ''}
                        onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                        placeholder="contact@school.edu"
                      />
                    </div>
                    <div>
                      <Label>Contact Phone</Label>
                      <Input
                        value={formData.contact_phone || ''}
                        onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                        placeholder="(512) 555-0100"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Park District Form */}
            {dialogType === 'park' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Code *</Label>
                    <Input
                      value={formData.code || ''}
                      onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                      placeholder="e.g., APF"
                      data-testid="park-code-input"
                    />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select 
                      value={formData.active ? 'true' : 'false'} 
                      onValueChange={(v) => setFormData({...formData, active: v === 'true'})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Active</SelectItem>
                        <SelectItem value="false">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={formData.name || ''}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Batavia Park District"
                    data-testid="park-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Contact Name</Label>
                    <Input
                      value={formData.contact_name || ''}
                      onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Contact Email</Label>
                    <Input
                      type="email"
                      value={formData.contact_email || ''}
                      onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>City</Label>
                    <Input
                      value={formData.city || ''}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input
                      value={formData.state || 'IL'}
                      onChange={(e) => setFormData({...formData, state: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <Label>Service Area (comma-separated cities)</Label>
                  <Input
                    value={Array.isArray(formData.service_area) ? formData.service_area.join(', ') : ''}
                    onChange={(e) => setFormData({
                      ...formData, 
                      service_area: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                    })}
                    placeholder="Austin, Round Rock, Cedar Park"
                    data-testid="park-service-area-input"
                  />
                </div>
              </>
            )}

            {/* Library Form */}
            {dialogType === 'library' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Code *</Label>
                    <Input
                      value={formData.code || ''}
                      onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                      placeholder="e.g., APL-CENT"
                      data-testid="library-code-input"
                    />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select 
                      value={formData.active ? 'true' : 'false'} 
                      onValueChange={(v) => setFormData({...formData, active: v === 'true'})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Active</SelectItem>
                        <SelectItem value="false">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Library Name *</Label>
                  <Input
                    value={formData.name || ''}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Austin Central Library"
                    data-testid="library-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Library System</Label>
                    <Input
                      value={formData.library_system || ''}
                      onChange={(e) => setFormData({...formData, library_system: e.target.value})}
                      placeholder="e.g., Austin Public Library"
                    />
                  </div>
                  <div>
                    <Label>Branch Name</Label>
                    <Input
                      value={formData.branch_name || ''}
                      onChange={(e) => setFormData({...formData, branch_name: e.target.value})}
                      placeholder="e.g., Central, Spicewood Springs"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Contact Name</Label>
                    <Input
                      value={formData.contact_name || ''}
                      onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Contact Email</Label>
                    <Input
                      type="email"
                      value={formData.contact_email || ''}
                      onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>City</Label>
                    <Input
                      value={formData.city || ''}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input
                      value={formData.state || 'IL'}
                      onChange={(e) => setFormData({...formData, state: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <Label>Service Area (comma-separated cities)</Label>
                  <Input
                    value={Array.isArray(formData.service_area) ? formData.service_area.join(', ') : ''}
                    onChange={(e) => setFormData({
                      ...formData, 
                      service_area: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                    })}
                    placeholder="Austin, Downtown, Northwest Austin"
                    data-testid="library-service-area-input"
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} data-testid="save-btn">
              {dialogMode === 'create' ? 'Create' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
